import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react';
import { useCallback } from 'react';

import { FLAG_FREE_PREMIUM_DOWNLOAD_MODAL } from '@/constants/growthbook-flags';
import { useIsLoggedIn, useSubscriptionType } from '@/hooks/auth';
import { ASSET_TYPE } from '@/types/asset';

import type { DownloadDecision } from '@/features/Downloads/Gateway/types';
import type { AssetGroup } from '@/types/asset';

/**
 * DEV-7218 A/B test arms for a logged-in free user hitting a premium Music
 * download. Control is the DialogStage premium journey (premium gate → usage →
 * plans); variant is the compact plan picker in the upsell side modal.
 */
export type FreePremiumDownloadArm = 'control' | 'variant';

/**
 * Returns a resolver for the user's arm, or `null` when they're outside the
 * test. The flag is only evaluated (and the user only enrolled) on a
 * download attempt that is actually in scope.
 */
export const useFreePremiumDownloadArm = () => {
  const growthbook = useGrowthBook();
  const newDownloadFlow = useFeatureIsOn('new-download-flow');
  const isLoggedIn = useIsLoggedIn();
  const subscriptionType = useSubscriptionType();

  return useCallback(
    (
      asset: AssetGroup,
      decision: DownloadDecision
    ): FreePremiumDownloadArm | null => {
      const isInAudience =
        newDownloadFlow &&
        isLoggedIn &&
        subscriptionType === 'Free' &&
        asset.type === ASSET_TYPE.MUSIC &&
        decision.action === 'upsell' &&
        decision.reason === 'premium-asset';

      if (!isInAudience) {
        return null;
      }

      return growthbook?.isOn(FLAG_FREE_PREMIUM_DOWNLOAD_MODAL)
        ? 'variant'
        : 'control';
    },
    [growthbook, newDownloadFlow, isLoggedIn, subscriptionType]
  );
};
